import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import PermissionResourceSchema from "permissions/resources/_schemas/permission-resource-schema";
import { AccessLevel, ResourceType, Scope } from "permissions/resources/permission-resource-types";
import { z } from "zod";

export type DiscountsAccessJSON = z.infer<typeof DiscountsAccessSchema.DiscountsAccessJSONZ>;

export type DiscountsAccessParams = z.infer<typeof DiscountsAccessSchema.DiscountsAccessZ>;

export default class DiscountsAccessSchema {
  public static readonly DiscountsAccessJSONZ = z.object({
    ...PermissionResourceSchema.ResourceJSONZ.shape,
    resourceType: z.literal(ResourceType.DiscountsAccess),
    accessLevel: z.enum(AccessLevel).nullish(),
    scope: z.enum(Scope).nullish(),
  });

  public static readonly DiscountsAccessZ = z.object({
    ...PermissionResourceSchema.ResourceZ.shape,
    resourceType: z.literal(ResourceType.DiscountsAccess),
    accessLevel: z.enum(AccessLevel).optional(),
    scope: z.enum(Scope).optional(),
  });

  public static validate(values: unknown): DiscountsAccessParams {
    return parseOrThrow(DiscountsAccessSchema.DiscountsAccessZ, values, {
      logError: true,
      schemaName: "DiscountsAccessSchema",
      operation: "validate",
      payloadSample: values,
    });
  }

  public static validateJSON(values: unknown): DiscountsAccessJSON {
    return parseOrThrow(DiscountsAccessSchema.DiscountsAccessJSONZ, values, {
      logError: true,
      schemaName: "DiscountsAccessSchema",
      operation: "validateJSON",
      payloadSample: values,
    });
  }

  public static isValid(values: unknown): values is DiscountsAccessParams {
    try {
      DiscountsAccessSchema.validate(values);
      return true;
    } catch {
      return false;
    }
  }

  public static isValidJSON(values: unknown): values is DiscountsAccessJSON {
    try {
      DiscountsAccessSchema.validateJSON(values);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(data: DiscountsAccessJSON): DiscountsAccessParams {
    const parsed = parseOrThrow(DiscountsAccessSchema.DiscountsAccessJSONZ, data, {
      logError: true,
      schemaName: "DiscountsAccessSchema",
      operation: "deserialize",
      payloadSample: data,
    });
    return DiscountsAccessSchema.codec.decode(parsed);
  }

  public static serialize(notification: DiscountsAccessParams): DiscountsAccessJSON {
    const parsed = DiscountsAccessSchema.validate(notification);
    return DiscountsAccessSchema.codec.encode(parsed);
  }

  private static readonly codec = z.codec(
    DiscountsAccessSchema.DiscountsAccessJSONZ,
    DiscountsAccessSchema.DiscountsAccessZ,
    {
      decode: (data) => ({
        ...PermissionResourceSchema.deserialize(data),
        resourceType: ResourceType.DiscountsAccess as const,
        accessLevel: data.accessLevel ?? undefined,
        scope: data.scope ?? undefined,
      }),
      encode: (resource) => ({
        ...PermissionResourceSchema.serialize(resource),
        resourceType: ResourceType.DiscountsAccess as const,
        accessLevel: resource.accessLevel,
        scope: resource.scope,
      }),
    }
  );
}
